import mixpanel from 'mixpanel-browser'

let initialized = false

/**
 * Analytics Composable - Mixpanel Integration
 * 
 * Provides methods for tracking events, identifying users, and managing analytics state.
 * All methods include validation, error handling, and privacy compliance.
 */
export function useAnalytics() {
  /**
   * Initialize Mixpanel with project token
   * Safe to call multiple times - will only initialize once
   */
  const init = () => {
    if (initialized || !import.meta.env.VITE_MIXPANEL_TOKEN) return
    
    try {
      mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
        debug: import.meta.env.DEV,
        track_pageview: true,
        persistence: 'localStorage'
      })
      initialized = true
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] Mixpanel initialized')
      }
    } catch (error) {
      console.error('[Analytics] Failed to initialize Mixpanel:', error)
    }
  }

  /**
   * Validate and clean event properties
   * - Removes null/undefined values
   * - Ensures data type consistency
   * - Adds timestamp in ISO 8601 format
   * 
   * @param {Object} properties - Raw properties object
   * @returns {Object} Validated and cleaned properties
   */
  const validateProperties = (properties) => {
    if (!properties || typeof properties !== 'object') return {}
    
    // Remove null/undefined values
    const cleaned = Object.entries(properties)
      .filter(([_, value]) => value !== null && value !== undefined)
      .reduce((acc, [key, value]) => {
        acc[key] = value
        return acc
      }, {})
    
    // Add timestamp in ISO 8601 format
    cleaned.timestamp = new Date().toISOString()
    
    return cleaned
  }

  /**
   * Track an event with properties
   * Includes validation, error handling, and automatic timestamp
   * 
   * @param {string} eventName - Name of the event (use ANALYTICS_EVENTS constants)
   * @param {Object} properties - Event properties (snake_case keys)
   */
  const track = (eventName, properties = {}) => {
    if (!initialized) {
      if (import.meta.env.DEV) {
        console.warn('[Analytics] Cannot track event - Mixpanel not initialized:', eventName)
      }
      return
    }

    try {
      const validatedProps = validateProperties(properties)
      mixpanel.track(eventName, validatedProps)
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] Event tracked:', eventName, validatedProps)
      }
    } catch (error) {
      console.error('[Analytics] Failed to track event:', eventName, error)
    }
  }

  /**
   * Identify a user and set their profile properties
   * 
   * @param {string} userId - Unique user identifier
   * @param {Object} traits - User properties (use USER_PROPERTIES constants)
   */
  const identify = (userId, traits = {}) => {
    if (!initialized) {
      if (import.meta.env.DEV) {
        console.warn('[Analytics] Cannot identify user - Mixpanel not initialized')
      }
      return
    }

    try {
      const validatedTraits = validateProperties(traits)
      mixpanel.identify(userId)
      mixpanel.people.set(validatedTraits)
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] User identified:', userId, validatedTraits)
      }
    } catch (error) {
      console.error('[Analytics] Failed to identify user:', error)
    }
  }

  /**
   * Set user properties (updates existing profile)
   * 
   * @param {Object} properties - User properties to set
   */
  const setUserProperties = (properties) => {
    if (!initialized) return

    try {
      const validatedProps = validateProperties(properties)
      mixpanel.people.set(validatedProps)
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] User properties set:', validatedProps)
      }
    } catch (error) {
      console.error('[Analytics] Failed to set user properties:', error)
    }
  }

  /**
   * Set super properties (included in all future events)
   * 
   * @param {Object} properties - Super properties to register
   */
  const setSuperProperties = (properties) => {
    if (!initialized) return

    try {
      const validatedProps = validateProperties(properties)
      mixpanel.register(validatedProps)
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] Super properties set:', validatedProps)
      }
    } catch (error) {
      console.error('[Analytics] Failed to set super properties:', error)
    }
  }

  /**
   * Increment a numeric user property
   * 
   * @param {string} property - Property name to increment
   * @param {number} value - Amount to increment (default: 1)
   */
  const incrementUserProperty = (property, value = 1) => {
    if (!initialized) return

    try {
      mixpanel.people.increment(property, value)
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] User property incremented:', property, value)
      }
    } catch (error) {
      console.error('[Analytics] Failed to increment property:', property, error)
    }
  }

  /**
   * Reset analytics state (call on logout)
   * Clears user identity and super properties
   */
  const reset = () => {
    if (!initialized) return

    try {
      mixpanel.reset()
      
      if (import.meta.env.DEV) {
        console.log('[Analytics] Analytics state reset')
      }
    } catch (error) {
      console.error('[Analytics] Failed to reset analytics:', error)
    }
  }

  return {
    init,
    identify,
    track,
    reset,
    setUserProperties,
    setSuperProperties,
    incrementUserProperty
  }
}
